import classNames from 'classnames'
import {FC, ReactNode, useRef, useEffect, useState} from 'react'

import {RenderIf, CopyButton} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {copyFormattedToClipboard} from 'utils'
import Toast from '../Toast'

interface ExpandableTextProps {
  children: ReactNode
  className?: string
  textClassName?: string
  collapsedClassName?: string
  breakAll?: boolean
  dataCy?: string
  showCopyButton?: boolean
  copyTooltipContent?: string
}

export const ExpandableText: FC<ExpandableTextProps> = ({
  children,
  className = '',
  textClassName = '',
  collapsedClassName = 'line-clamp-2',
  breakAll = true,
  dataCy,
  showCopyButton = false,
  copyTooltipContent,
}) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [isTextTruncated, setIsTextTruncated] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleExpandToggle = (): void => {
    setIsExpanded(!isExpanded)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === KEY_CODES.ENTER || e.key === KEY_CODES.SPACE) {
      e.preventDefault()
      handleExpandToggle()
    }
  }

  const onCopyButtonClick = (): void => {
    copyFormattedToClipboard(children as string)
    Toast({
      type: 'success',
      message: 'Copied!',
    })
  }

  useEffect(() => {
    const element = textRef.current
    if (element) {
      setIsTextTruncated(element.scrollHeight > element.clientHeight)
    }

    // Reset expanded state when children change
    setIsExpanded(false)
  }, [children])

  // Extract nested ternary operation into independent statement
  const getAriaLabel = (): string | undefined => {
    if (!isTextTruncated) return undefined
    return isExpanded ? 'Click to collapse text' : 'Click to expand text'
  }

  return (
    <div className={classNames('relative', className)}>
      <div className="relative">
        <div
          className={classNames('pr-2', {
            [collapsedClassName]: !isExpanded,
          })}
        >
          <div
            className={classNames(
              {
                'break-all': breakAll,
                'cursor-pointer': isTextTruncated,
              },
              textClassName,
            )}
            data-cy={dataCy}
            ref={textRef}
            {...(isTextTruncated && {
              role: 'button',
              tabIndex: 0,
              onClick: handleExpandToggle,
              onKeyDown: handleKeyDown,
              'aria-label': getAriaLabel(),
            })}
          >
            {children}
            <RenderIf isTrue={showCopyButton}>
              <CopyButton
                onClick={onCopyButtonClick}
                tooltipContent={copyTooltipContent}
                className="inline-block ml-1 align-middle"
              />
            </RenderIf>
          </div>
        </div>
      </div>
    </div>
  )
}
